import React from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Button Atom
 * A base button component that applies our design system styles
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.variant - Button variant ('contained', 'outlined', 'text')
 * @param {string} props.color - Button color from theme
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 * @param {boolean} props.fullWidth - Whether button should take full width
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {boolean} props.loading - Whether to show loading state
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.startIcon - Icon to show at the start
 * @param {React.ReactNode} props.endIcon - Icon to show at the end
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} Button component
 */
const Button = ({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  loading = false,
  onClick,
  startIcon,
  endIcon,
  sx = {},
  ...rest
}) => {
  // If loading, show CircularProgress as start icon and disable button
  const finalStartIcon = loading ? <CircularProgress size={20} color="inherit" /> : startIcon;
  const isDisabled = disabled || loading;

  return (
    <MuiButton
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={isDisabled}
      onClick={onClick}
      startIcon={finalStartIcon}
      endIcon={loading ? null : endIcon}
      sx={sx}
      {...rest}
    >
      {children}
    </MuiButton>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  color: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  sx: PropTypes.object
};

export default Button;